import { type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { Wordmark } from "@/components/Wordmark";

import "@/styles/tokens-v2-components.css";
import "./AuthLayout.css";

export interface AuthLayoutProps {
  /** Form panel contents — the page's own form markup. */
  children: ReactNode;
  /**
   * Title rendered at the top of the form panel
   * ("Welcome back.", "Create your workspace.", "Reset your password.").
   */
  title: string;
  /** Optional subtitle below the title. */
  subtitle?: string;
  /**
   * When true, hides the segmented Sign-in / Create-account tab control.
   * `/change-password` uses this — it's reached from inside the app and the
   * "switch to register" affordance would be misleading.
   */
  hideTabs?: boolean;
  /**
   * When true, collapses to a single-column form-only layout (the brand
   * panel is hidden). Used by `/change-password` per the Phase 3 plan.
   */
  singleColumn?: boolean;
}

/**
 * Two-column auth shell from `New UI Handoff/screens/auth-v2.html`.
 *
 *  - Left: brand wordmark + pitch line + a sample transaction-receipt card.
 *    The pitch copy is static; the receipt is decorative and unrelated to
 *    the user's data.
 *  - Right: a segmented `Sign in | Create account` tab control + the page
 *    form (passed as `children`) + a legal footer.
 *
 * Below 900px the left panel hides and the form takes the full width.
 *
 * Active tab is derived from `useLocation().pathname` so the same layout
 * file works for `/login`, `/register`, and `/change-password` (which
 * passes `hideTabs` + `singleColumn`).
 */
export function AuthLayout({
  children,
  title,
  subtitle,
  hideTabs = false,
  singleColumn = false,
}: AuthLayoutProps) {
  const location = useLocation();
  const isRegister = location.pathname === "/register";

  return (
    <div
      className={`auth-shell${singleColumn ? " auth-shell--single" : ""}`}
      data-testid="auth-shell"
    >
      <aside className="auth-pitch" aria-hidden={singleColumn || undefined}>
        <div className="auth-pitch-top">
          <Wordmark size="md" onDark aria-label="Cawamba" />
          <span className="auth-pitch-status">
            <span className="live" aria-hidden />
            14 chains synced &middot; 2s ago
          </span>
        </div>

        <div className="auth-pitch-copy">
          <h2>
            Crypto bookkeeping{" "}
            <span className="accent">your auditor will sign off on.</span>
          </h2>
          <p>
            Add the context block-explorers don&rsquo;t store &mdash; comments,
            tags, owners &mdash; at the wallet, walletToken, and transaction
            level. Then export it straight to NetSuite, QuickBooks, or Xero.
          </p>

          {/* Decorative receipt card — illustrates a tagged, memo'd tx. */}
          <div className="auth-receipt" aria-hidden>
            <div className="auth-receipt-head">
              <div className="auth-receipt-dots">
                <span />
                <span />
                <span />
              </div>
              <span>cawamba ~ /tx/0x7a2f&hellip;42b9</span>
            </div>
            <div className="auth-receipt-body">
              <div className="auth-receipt-row">
                <span className="k">// hash</span>
                <span className="v">0x7a2f&hellip;42b9</span>
              </div>
              <div className="auth-receipt-row">
                <span className="k">// amount</span>
                <span className="v pos">+ 12,500.00 USDC</span>
              </div>
              <div className="auth-receipt-row">
                <span className="k">// wallet</span>
                <span className="v sans">Treasury &middot; Hot</span>
              </div>
              <div className="auth-receipt-row">
                <span className="k">// tags</span>
                <span className="v">
                  <span className="auth-receipt-tags">
                    <span className="t b1">customer-payment</span>
                    <span className="t b2">stripe-payout</span>
                    <span className="t b3">acme-corp</span>
                  </span>
                </span>
              </div>
              <div className="auth-receipt-row">
                <span className="k">// memo</span>
                <span className="v sans memo">
                  Stripe payout for April invoices &middot; matched to NetSuite
                  #INV-4421
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-pitch-foot">
          <span>// btc &middot; eth &middot; usdt &middot; usdc &middot; trx &middot; xrp &middot; +12</span>
          <span>// soc 2 &middot; audit-ready</span>
        </div>
      </aside>

      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          {!hideTabs && (
            <nav className="auth-tabs" aria-label="Authentication">
              <Link
                to="/login"
                className={isRegister ? "" : "on"}
                aria-current={isRegister ? undefined : "page"}
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className={isRegister ? "on" : ""}
                aria-current={isRegister ? "page" : undefined}
              >
                Create account
              </Link>
            </nav>
          )}

          <h1 className="auth-title">{title}</h1>
          {subtitle ? <p className="auth-subtitle">{subtitle}</p> : null}

          {children}

          <p className="auth-legal">
            By continuing you agree to Cawamba&rsquo;s{" "}
            <a href="/terms">Terms of Service</a> and{" "}
            <a href="/privacy">Privacy Policy</a>. Cawamba is watch-only &mdash;
            we never custody funds and never request keys.
          </p>
        </div>
      </section>
    </div>
  );
}
